/*hook to use in components shared between DPS and PP to avoid writing a complicated fetch function each time */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import {
    type DocumentDownloadResponse,
    type GetDPSDetailResponse,
    type GetDPSTransactionsGetRequest,
    type GetLifeTransactionsGetRequest,
    type GetLifeTransactionsResponse,
    type GetPensionTransactionsResponse,
    type GetPPDetailResponse,
    type GetPPTransactionsGetRequest,
    ProductType,
} from "@repo/api";
import {
    assertIsPensionContractType,
    assertIsSupportedContractType,
    type PensionContractType,
    type SupportedContractType,
} from "@repo/logic";
import { useContractTypeContext } from "../context/ContractTypeContext";
import { usePrimaryAPIClient } from "../context/PrimaryAPIClient";
import { useQueryKeyFactory } from "../context/QueryKeyProvider";

type IPensionDetailProps<TResult = GetDPSDetailResponse | GetPPDetailResponse> = {
    id: string;
    type?: PensionContractType;
    options?: Partial<UseQueryOptions<GetDPSDetailResponse | GetPPDetailResponse, Error, TResult>>;
};

export const usePensionDetail = <TResult = GetDPSDetailResponse | GetPPDetailResponse,>({
    id,
    type,
    options,
}: IPensionDetailProps<TResult>) => {
    const { contractsDpsApi, contractsPpApi } = usePrimaryAPIClient();
    const { contractType } = useContractTypeContext();
    const { createQueryKey } = useQueryKeyFactory();

    const pensionProductType = type || contractType;

    const fetchMethod = () => {
        assertIsPensionContractType(
            pensionProductType,
            "No or wrong contract type provided to use pension detail hook",
        );

        if (pensionProductType === ProductType.Uf) {
            return contractsDpsApi.getDPSDetailGet({ contractId: id });
        }
        return contractsPpApi.getPPDetailGet({ contractId: id });
    };

    return useQuery<GetDPSDetailResponse | GetPPDetailResponse, Error, TResult>({
        queryKey: createQueryKey({
            key: ["getContractDetail", id, pensionProductType],
            includeUserIdentifier: false,
        }),
        queryFn: fetchMethod,
        throwOnError: false,
        ...options,
    });
};

type IPensionDocumentsProps = {
    id: string;
    type: PensionContractType;
    options?: Partial<UseQueryOptions<DocumentDownloadResponse[]>>;
};

export const usePensionDocuments = ({ id, type, options }: IPensionDocumentsProps) => {
    const { documentsApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    const fetchMethod = () => {
        assertIsPensionContractType(type, "No or wrong contract type provided to use pension documents hook");

        if (type === ProductType.Uf) {
            return documentsApi.getDPSDocumentsGet({ contractId: id });
        }
        return documentsApi.getPPDocumentsGet({ contractId: id });
    };

    return useQuery<DocumentDownloadResponse[]>({
        ...options,
        queryKey: createQueryKey({
            key: ["getPensionDocuments", id],
            includeUserIdentifier: false,
        }),
        queryFn: fetchMethod,
    });
};

type TransactionsProps = {
    type: SupportedContractType;
    params: Pick<
        GetDPSTransactionsGetRequest | GetLifeTransactionsGetRequest | GetPPTransactionsGetRequest,
        "contractId" | "count"
    >;
    options?: Partial<UseQueryOptions<GetLifeTransactionsResponse[] | GetPensionTransactionsResponse[]>>;
};

export const useTransactions = ({ type, params, options }: TransactionsProps) => {
    const { contractsDpsApi, contractsPpApi, contractsLifeApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    const fetchMethodByProductType = {
        [ProductType.Uf]: async (params: GetDPSTransactionsGetRequest) => contractsDpsApi.getDPSTransactionsGet(params),
        [ProductType.Pf]: async (params: GetPPTransactionsGetRequest) => contractsPpApi.getPPTransactionsGet(params),
        [ProductType.Cl]: async (params: GetLifeTransactionsGetRequest) =>
            contractsLifeApi.getLifeTransactionsGet({
                ...params,
                productType: ProductType.Cl,
            }),
        [ProductType.Clf]: async (params: GetLifeTransactionsGetRequest) =>
            contractsLifeApi.getLifeTransactionsGet({
                ...params,
                productType: ProductType.Clf,
            }),
    };

    const fetchMethod = async () => {
        assertIsSupportedContractType(type);
        return await fetchMethodByProductType[type](params);
    };

    return useQuery<GetLifeTransactionsResponse[] | GetPensionTransactionsResponse[]>({
        queryKey: createQueryKey({
            key: ["getTransactions", params],
            includeUserIdentifier: false,
        }),
        queryFn: () => {
            return fetchMethod();
        },
        ...options,
    });
};
